import React, { useMemo, useState } from "react";
import {
    FiHelpCircle,
    FiSearch,
    FiSend,
    FiCheckCircle,
} from "react-icons/fi";
import { PageHeader, useToast } from "../../theme/components";
import { PageButton } from "../../theme/components/PageButton";
import { contactApi } from "../../api/endpoints/contact";
import {
    CONTACT_TOPICS,
    OTHER_TOPIC,
    searchTopics,
} from "../../utils/contactTopics";
import "./Help.css";

const MAX_MESSAGE = 4000;

const Help = () => {
    const toast = useToast();
    const [query, setQuery] = useState("");
    const [topic, setTopic] = useState("");
    const [customReason, setCustomReason] = useState("");
    const [message, setMessage] = useState("");
    const [touched, setTouched] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submittedId, setSubmittedId] = useState(null);

    const filtered = useMemo(() => searchTopics(query), [query]);

    const messageError =
        touched.message && message.trim().length === 0 ? "Required" : null;
    const reasonError =
        touched.customReason && topic === OTHER_TOPIC && customReason.trim() === ""
            ? "Required"
            : null;
    const topicError = touched.topic && !topic ? "Please pick a topic" : null;

    const handleTopicPick = (value) => {
        setTopic(value);
        setTouched((prev) => ({ ...prev, topic: true }));
        if (value !== OTHER_TOPIC) setCustomReason("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const next = { topic: true, message: true, customReason: topic === OTHER_TOPIC };
        setTouched(next);
        if (!topic || message.trim() === "") {
            toast.error("Please fill in the required fields before sending.");
            return;
        }
        if (topic === OTHER_TOPIC && customReason.trim() === "") {
            toast.error("Please describe your reason.");
            return;
        }
        setSubmitting(true);
        try {
            const created = await contactApi.create({
                topic,
                customReason: topic === OTHER_TOPIC ? customReason.trim() : null,
                message: message.trim(),
            });
            setSubmittedId(created.id);
            toast.success("Message sent. The team will follow up via email.");
            setTopic("");
            setCustomReason("");
            setMessage("");
            setQuery("");
            setTouched({});
        } catch (err) {
            const detail =
                err?.response?.data?.message ||
                err?.response?.data?.detail ||
                err?.message ||
                "Failed to send message";
            toast.error(detail);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="help-wrapper">
            <PageHeader
                title="Help & Support"
                subtitle="Pick a topic, describe what's happening, and we'll get back to you."
                icon={FiHelpCircle}
            />

            <div className="help-body">
                {submittedId !== null && (
                    <div className="help-success" role="status">
                        <FiCheckCircle size={18} />
                        <span>
                            Thanks! Your message was received. Reference:{" "}
                            <strong>#{submittedId}</strong>
                        </span>
                    </div>
                )}

                <form className="help-form" onSubmit={handleSubmit} noValidate>
                    <div className="help-form-group help-full-width">
                        <label>
                            What can we help with? <span className="req">*</span>
                        </label>
                        <div className="help-search">
                            <FiSearch className="help-search-icon" />
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search topics (e.g. login, bug, vaccine)"
                                aria-label="Search topics"
                            />
                        </div>
                        {topicError && <span className="help-error-text">{topicError}</span>}

                        <div className="help-topics">
                            {filtered.length === 0 && (
                                <div className="help-topics-empty">
                                    No topics match “{query}”. Try a different word, or
                                    pick “Other” to describe it.
                                </div>
                            )}
                            {filtered.map((t) => {
                                const active = topic === t.value;
                                return (
                                    <button
                                        key={t.value}
                                        type="button"
                                        className={`help-topic-pill ${
                                            active ? "is-active" : ""
                                        } help-topic-pill--${t.value}`}
                                        onClick={() => handleTopicPick(t.value)}
                                        aria-pressed={active}
                                    >
                                        {t.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {topic === OTHER_TOPIC && (
                        <div className="help-form-group help-full-width">
                            <label htmlFor="customReason">
                                Reason <span className="req">*</span>
                            </label>
                            <div
                                className={`cr-input-box ${
                                    reasonError ? "has-error" : ""
                                }`}
                            >
                                <input
                                    id="customReason"
                                    type="text"
                                    value={customReason}
                                    onChange={(e) => setCustomReason(e.target.value)}
                                    onBlur={() =>
                                        setTouched((p) => ({ ...p, customReason: true }))
                                    }
                                    placeholder="Briefly describe what's going on (e.g. mobile app crashes on save)"
                                    maxLength={200}
                                />
                            </div>
                            {reasonError && (
                                <span className="help-error-text">{reasonError}</span>
                            )}
                        </div>
                    )}

                    <div className="help-form-group help-full-width">
                        <label htmlFor="message">
                            Message <span className="req">*</span>
                        </label>
                        <div
                            className={`cr-input-box cr-textarea-box ${
                                messageError ? "has-error" : ""
                            }`}
                        >
                            <textarea
                                id="message"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                onBlur={() =>
                                    setTouched((p) => ({ ...p, message: true }))
                                }
                                placeholder="Share as much detail as you can — what you did, what you expected, and what happened."
                                rows={6}
                                maxLength={MAX_MESSAGE}
                            />
                        </div>
                        <div className="help-form-meta">
                            {messageError && (
                                <span className="help-error-text">{messageError}</span>
                            )}
                            <span className="help-counter">
                                {message.length} / {MAX_MESSAGE}
                            </span>
                        </div>
                    </div>

                    <div className="help-form-footer help-full-width">
                        <span className="help-footer-note">
                            Your message is sent as <strong>{CONTACT_TOPICS.find((t) => t.value === topic)?.label || "—"}</strong>.
                        </span>
                        <PageButton
                            type="submit"
                            variant="primary"
                            startIcon={<FiSend />}
                            disabled={submitting}
                        >
                            {submitting ? "Sending…" : "Send message"}
                        </PageButton>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Help;
